import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {
  Box,
  ButtonBase,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {alpha, useTheme} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import SearchIcon from '@material-ui/icons/Search';
import {VariableSizeList} from 'react-window';
import {useHistory} from 'react-router-dom';
import moment from 'moment';
import {formatPanelChangeDetailTitle} from '../helpers/panelChangeGuides';
import {usePanelChangeListStyles} from '../helpers/panelChangeListStyles';
import {
  buildAdUnitSelection,
  buildPanelChangeActorOptions,
  computeDetailPanelPosition,
  dedupePanelChanges,
  filterPanelChanges,
  filterPanelChangesByActor,
  getPanelChangeRowDisplay,
  resolvePanelChangeActors,
  groupChangesByAdUnit,
} from '../helpers/panelChangeLog';
import PanelChangeSummary from './PanelChangeSummary';
import PanelChangeRow from './PanelChangeRow';
import PanelChangeAdUnitLink from './PanelChangeAdUnitLink';
import {PanelChangeActorFilterOption} from './PanelChangeActors';
import useStore from '../../../../store/store';
import usePersistentAuthStore from '../../../../store/persistentAuthStore';

const PANEL_WIDTH = 380;
const PANEL_MAX_HEIGHT = 300;
const PANEL_HEADER_HEIGHT = 44;
const SEARCH_BAR_HEIGHT = 48;
const TAB_SUMMARY = 'summary';
const TAB_ALL = 'all';

const getGlassSurface = theme => ({
  backgroundColor:
    theme.palette.type === 'dark' ? 'rgba(0,0,0,0.65)' : 'rgba(255,255,255,0.65)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
});

// Menu Paper keeps MuiPaper-root's opaque fill; override it and use a lighter tint so
// it doesn't stack to a solid slab when opened over the changelog glass.
const getMenuGlassSurface = theme => ({
  backgroundColor:
    theme.palette.type === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  backgroundImage: 'none',
  border: 'none',
  marginBottom: 6,
});

const useStyles = makeStyles(theme => {
  const glassSurface = getGlassSurface(theme);

  return {
  root: {
    position: 'absolute',
    zIndex: 30,
    width: PANEL_WIDTH,
    maxWidth: 'calc(100% - 16px)',
  },
  panel: {
    width: '100%',
    height: PANEL_MAX_HEIGHT,
    maxHeight: PANEL_MAX_HEIGHT,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[8],
    ...glassSurface,
  },
  arrowDownClip: {
    position: 'absolute',
    top: '100%',
    width: 16,
    height: 10,
    overflow: 'hidden',
    transform: 'translateX(-50%)',
    pointerEvents: 'none',
    zIndex: 2,
  },
  arrowDown: {
    position: 'absolute',
    top: -6,
    left: '50%',
    width: 12,
    height: 12,
    marginLeft: -6,
    transform: 'rotate(45deg)',
    ...glassSurface,
    borderRight: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[2],
    pointerEvents: 'none',
  },
  arrowUpClip: {
    position: 'absolute',
    bottom: '100%',
    width: 16,
    height: 10,
    overflow: 'hidden',
    transform: 'translateX(-50%)',
    pointerEvents: 'none',
    zIndex: 2,
  },
  arrowUp: {
    position: 'absolute',
    bottom: -6,
    left: '50%',
    width: 12,
    height: 12,
    marginLeft: -6,
    transform: 'rotate(45deg)',
    ...glassSurface,
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderTop: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[2],
    pointerEvents: 'none',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    flexShrink: 0,
    padding: theme.spacing(0.75, 0.75, 0.75, 1.25),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  dateLabel: {
    flex: 1,
    minWidth: 0,
    fontSize: '0.8125rem',
    fontWeight: 600,
    lineHeight: 1.3,
  },
  tabs: {
    display: 'inline-flex',
    flexShrink: 0,
    gap: 2,
    padding: 2,
    borderRadius: 6,
    backgroundColor:
      theme.palette.type === 'dark'
        ? alpha(theme.palette.common.white, 0.06)
        : alpha(theme.palette.common.black, 0.04),
  },
  tab: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    padding: theme.spacing(0.375, 0.625),
    borderRadius: 5,
    fontSize: '0.6875rem',
    fontWeight: 600,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    color: theme.palette.text.secondary,
    transition: 'background-color 120ms ease, color 120ms ease, box-shadow 120ms ease',
    '&:hover': {
      color: theme.palette.text.primary,
    },
  },
  tabActive: {
    color: theme.palette.primary.main,
    backgroundColor:
      theme.palette.type === 'dark'
        ? alpha(theme.palette.primary.main, 0.18)
        : theme.palette.background.paper,
    boxShadow:
      theme.palette.type === 'dark' ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.08)',
  },
  tabContent: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  closeButton: {
    flexShrink: 0,
    margin: theme.spacing(-0.25, 0),
  },
  actorFilterButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
    minHeight: 24,
    padding: theme.spacing(0.25, 0.5),
    borderRadius: 5,
    fontSize: '0.6875rem',
    fontWeight: 600,
    lineHeight: 1.2,
    color: theme.palette.text.secondary,
    backgroundColor:
      theme.palette.type === 'dark'
        ? alpha(theme.palette.common.white, 0.06)
        : alpha(theme.palette.common.black, 0.04),
    transition: 'background-color 120ms ease, color 120ms ease',
    '&:hover': {
      color: theme.palette.text.primary,
      backgroundColor:
        theme.palette.type === 'dark'
          ? alpha(theme.palette.common.white, 0.1)
          : alpha(theme.palette.common.black, 0.06),
    },
  },
  actorFilterButtonActive: {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.14),
    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
    },
  },
  actorFilterIcon: {
    fontSize: '0.95rem',
  },
  actorFilterCount: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    lineHeight: 1,
  },
  actorFilterMenuPaper: {
    // Beat MuiPaper-root opaque background.paper (concatenated onto this class)
    '&&': {
      ...getMenuGlassSurface(theme),
      boxShadow: theme.shadows[4],
    },
  },
  actorFilterMenuItem: {
    fontSize: '0.8125rem',
    minHeight: 36,
    backgroundColor: 'transparent',
  },
  actorFilterMenuList: {
    backgroundColor: 'transparent',
    padding: 0,
  },
  searchBar: {
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  searchInput: {
    fontSize: '0.8125rem',
  },
  listVirtual: {
    overflow: 'hidden',
    padding: 0,
  },
  virtualRowInner: {
    // flow-root contains child margins so the measured height is accurate; the bottom padding
    // provides the inter-row gap (row margins collapse to 0 when each row is isolated).
    display: 'flow-root',
    padding: theme.spacing(0, 1.5, 0.75),
  },
  virtualRowInnerHeader: {
    display: 'flow-root',
    padding: theme.spacing(0, 1.5, 0),
  },
};
});

// Above this many rows we virtualize the list; below it we render plainly so the popover
// shrinks to its content instead of reserving a fixed-height scroll area.
const VIRTUALIZE_THRESHOLD = 30;
// Over-estimate slightly: an unmeasured row then reserves too much (a gap) instead of
// too little (overlap) until its real height is measured.
const ESTIMATED_ROW_HEIGHT = 118;
const ESTIMATED_ROW_HEIGHT_COMPACT = 88;
const ESTIMATED_HEADER_HEIGHT = 28;

const AdUnitHeader = ({classes, group, onOpen}) => (
  <Box className={classes.groupHeader}>
    <PanelChangeAdUnitLink classes={classes} unit={group} onOpen={onOpen} />
    <Typography variant="caption" className={classes.groupCount}>
      {group.changes.length}
    </Typography>
  </Box>
);

const ChangeRow = ({classes, change, flush, attributeGroups, actorView}) => {
  const display = getPanelChangeRowDisplay(change, attributeGroups);

  return (
    <PanelChangeRow
      classes={classes}
      primaryLine={display.primaryLine}
      secondaryLine={display.secondaryLine}
      oldValue={display.oldValue}
      newValue={display.newValue}
      valueTone={display.valueTone}
      singleValue={display.singleValue}
      showValues={display.showValues}
      sspLogo={display.sspLogo}
      isTagLevel={display.isTagLevel}
      actors={resolvePanelChangeActors([change], actorView?.auth, actorView?.show)}
      footer={moment(change.timestamp).format('HH:mm:ss')}
      flush={flush}
    />
  );
};

/** Row renderer for the virtualized list; measures its own height and reports it back. */
const VirtualRow = ({index, style, data}) => {
  const {items, classes, panelClasses, setSize, onOpenAdUnit, attributeGroups, actorView} = data;
  const item = items[index];
  const rowRef = useRef(null);

  useLayoutEffect(() => {
    const el = rowRef.current;
    if (!el) return undefined;

    const measure = () => setSize(index, el.getBoundingClientRect().height);
    measure();

    // Re-measure if the row's height changes after mount (text wrapping, web-font load),
    // otherwise late reflows leave rows overlapping until the user scrolls.
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [index, item, setSize]);

  return (
    <div style={style}>
      <div
        ref={rowRef}
        className={
          item.type === 'header'
            ? panelClasses.virtualRowInnerHeader
            : panelClasses.virtualRowInner
        }
      >
        {item.type === 'header' ? (
          <AdUnitHeader classes={classes} group={item.group} onOpen={() => onOpenAdUnit(item.group)} />
        ) : (
          <ChangeRow
            classes={classes}
            change={item.change}
            flush={item.flush}
            attributeGroups={attributeGroups}
            actorView={actorView}
          />
        )}
      </div>
    </div>
  );
};

const PanelChangeDetailPanel = ({
  date,
  changes = [],
  period,
  anchorRect,
  containerRef,
  onClose,
}) => {
  const classes = useStyles();
  const listClasses = usePanelChangeListStyles();
  const theme = useTheme();
  const history = useHistory();
  const {auth} = usePersistentAuthStore();
  const setStoreState = useStore(state => state.setState);
  const currentSelection = useStore(state => state.selection);
  const analyticsAttributeGroups = useStore(state => state.analyticsAttributeGroups);
  const [position, setPosition] = useState(null);
  const [search, setSearch] = useState('');
  const [actorFilter, setActorFilter] = useState('all');
  const [actorMenuAnchor, setActorMenuAnchor] = useState(null);
  const [activeTab, setActiveTab] = useState(TAB_SUMMARY);

  // When already viewing a single ad unit, its name in the log is redundant – hide that header.
  const selectedPlacementId = currentSelection?.value?.placement ?? null;
  const isSingleAdUnitSelected = selectedPlacementId != null;

  // Reset tab and search whenever a different bucket is opened.
  useEffect(() => {
    setSearch('');
    setActorFilter('all');
    setActorMenuAnchor(null);
    setActiveTab(TAB_SUMMARY);
  }, [date]);

  useEffect(() => {
    if (isSingleAdUnitSelected) {
      setActiveTab(TAB_SUMMARY);
    }
  }, [isSingleAdUnitSelected]);

  const uniqueChanges = useMemo(() => dedupePanelChanges(changes), [changes]);
  const actorOptions = useMemo(
    () => buildPanelChangeActorOptions(uniqueChanges, auth),
    [uniqueChanges, auth]
  );
  const actorCount = Math.max(actorOptions.length - 1, 0);
  const showActorFilter = actorCount > 1 && uniqueChanges.length > 0;
  const selectedActorOption = actorOptions.find(option => option.key === actorFilter);
  const actorView = useMemo(
    () => ({auth, show: actorFilter === 'all'}),
    [auth, actorFilter]
  );

  const actorFilteredChanges = useMemo(
    () => filterPanelChangesByActor(uniqueChanges, actorFilter),
    [uniqueChanges, actorFilter]
  );

  const filteredChanges = useMemo(
    () => filterPanelChanges(actorFilteredChanges, search),
    [actorFilteredChanges, search]
  );

  const groupedChanges = useMemo(() => groupChangesByAdUnit(filteredChanges), [filteredChanges]);

  const handleSelectAdUnit = useCallback(
    group => {
      const resolved = buildAdUnitSelection(group, {auth, currentSelection});
      if (!resolved) return;

      const {publisher, domain, placement} = resolved.value;
      history.push(`/home/${publisher}/${domain}/${placement}`);
      setStoreState('calculated', null);
      setStoreState('selection', resolved);
      onClose?.();
    },
    [auth, currentSelection, history, setStoreState, onClose]
  );

  // Flatten groups into a single list (headers + rows) for virtualized rendering.
  const items = useMemo(() => {
    const out = [];
    for (const group of groupedChanges) {
      const isSelectedUnit =
        selectedPlacementId != null &&
        group.adunitId != null &&
        String(group.adunitId) === String(selectedPlacementId);

      if (!isSelectedUnit) {
        out.push({type: 'header', key: `h-${group.key}`, group});
      }
      group.changes.forEach((change, index) => {
        out.push({
          type: 'change',
          key: `c-${change.id}-${change.adunitId ?? index}`,
          change,
          flush: isSelectedUnit,
        });
      });
    }
    return out;
  }, [groupedChanges, selectedPlacementId]);

  const shouldVirtualize = filteredChanges.length > VIRTUALIZE_THRESHOLD;
  const showSearchBar =
    actorFilteredChanges.length > 5 && activeTab === TAB_ALL && !isSingleAdUnitSelected;
  const listVirtualHeight =
    PANEL_MAX_HEIGHT - PANEL_HEADER_HEIGHT - (showSearchBar ? SEARCH_BAR_HEIGHT : 0);

  const renderHeaderActorFilter = () => {
    if (!showActorFilter) return null;

    const tooltip = actorFilter !== 'all'
      ? `Filtered: ${selectedActorOption?.label}`
      : `${actorCount} users changed settings`;

    return (
      <>
        <Tooltip title={tooltip}>
          <ButtonBase
            className={`${classes.actorFilterButton} ${
              actorFilter !== 'all' ? classes.actorFilterButtonActive : ''
            }`}
            onClick={event => setActorMenuAnchor(event.currentTarget)}
            aria-label="Filter by who changed"
            aria-haspopup="menu"
            aria-expanded={Boolean(actorMenuAnchor)}
          >
            {actorFilter !== 'all' ? (
              <PanelChangeActorFilterOption
                classes={listClasses}
                option={selectedActorOption}
                compact
              />
            ) : (
              <>
                <PeopleOutlineIcon className={classes.actorFilterIcon} />
                <span className={classes.actorFilterCount}>{actorCount}</span>
              </>
            )}
          </ButtonBase>
        </Tooltip>
        <Menu
          anchorEl={actorMenuAnchor}
          open={Boolean(actorMenuAnchor)}
          onClose={() => setActorMenuAnchor(null)}
          anchorOrigin={{vertical: 'top', horizontal: 'right'}}
          transformOrigin={{vertical: 'top', horizontal: 'right'}}
          getContentAnchorEl={null}
          classes={{paper: classes.actorFilterMenuPaper, list: classes.actorFilterMenuList}}
          PaperProps={{elevation: 0, style: getMenuGlassSurface(theme)}}
          MenuListProps={{disablePadding: true}}
        >
          {actorOptions.map(option => (
            <MenuItem
              key={option.key}
              selected={option.key === actorFilter}
              onClick={() => {
                setActorFilter(option.key);
                setActorMenuAnchor(null);
              }}
              className={classes.actorFilterMenuItem}
            >
              <PanelChangeActorFilterOption classes={listClasses} option={option} />
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  };

  const listRef = useRef(null);
  const sizeMapRef = useRef({});
  const prevItemsRef = useRef(items);

  // Drop stale measurements synchronously when the visible items change (search, day, selection),
  // i.e. BEFORE the rows re-measure in their layout effects. Doing this in a post-commit effect
  // would wipe the fresh measurements and leave rows overlapping until the next scroll.
  if (prevItemsRef.current !== items) {
    prevItemsRef.current = items;
    sizeMapRef.current = {};
  }

  const setSize = useCallback((index, size) => {
    if (size && sizeMapRef.current[index] !== size) {
      sizeMapRef.current[index] = size;
      listRef.current?.resetAfterIndex(index);
    }
  }, []);

  const getItemSize = useCallback(
    index =>
      sizeMapRef.current[index] ||
      (items[index]?.type === 'header'
        ? ESTIMATED_HEADER_HEIGHT
        : actorView.show
          ? ESTIMATED_ROW_HEIGHT
          : ESTIMATED_ROW_HEIGHT_COMPACT),
    [items, actorView.show]
  );

  // Parent layout effects run after the rows' (children) layout effects, so by now every
  // visible row has reported its measured height into sizeMapRef. Force one clean recompute
  // from index 0 — pre-paint — so the first frame already uses real heights (no overlap that
  // only resolves after scrolling).
  useLayoutEffect(() => {
    listRef.current?.resetAfterIndex(0);
  }, [items]);

  const virtualItemData = useMemo(
    () => ({
      items,
      classes: listClasses,
      panelClasses: classes,
      setSize,
      onOpenAdUnit: handleSelectAdUnit,
      attributeGroups: analyticsAttributeGroups,
      actorView,
    }),
    [items, listClasses, classes, setSize, handleSelectAdUnit, analyticsAttributeGroups, actorView]
  );

  useLayoutEffect(() => {
    setPosition(
      computeDetailPanelPosition(anchorRect, containerRef?.current, {
        width: PANEL_WIDTH,
        maxHeight: PANEL_MAX_HEIGHT,
      })
    );
  }, [anchorRect, containerRef, date]);

  if (!date) return null;

  return (
    <Box
      className={classes.root}
      style={
        position
          ? {left: position.left, top: position.top}
          : {visibility: 'hidden', pointerEvents: 'none'}
      }
    >
      <Paper className={classes.panel} elevation={8}>
      <Box className={classes.header}>
        <Typography variant="subtitle2" className={classes.dateLabel} noWrap>
          {formatPanelChangeDetailTitle(date, period)}
        </Typography>
        {uniqueChanges.length > 0 && !isSingleAdUnitSelected && (
          <Box className={classes.tabs} role="tablist" aria-label="Change log views">
            <ButtonBase
              className={`${classes.tab} ${activeTab === TAB_SUMMARY ? classes.tabActive : ''}`}
              onClick={() => setActiveTab(TAB_SUMMARY)}
              role="tab"
              aria-selected={activeTab === TAB_SUMMARY}
            >
              Summary
            </ButtonBase>
            <ButtonBase
              className={`${classes.tab} ${activeTab === TAB_ALL ? classes.tabActive : ''}`}
              onClick={() => setActiveTab(TAB_ALL)}
              role="tab"
              aria-selected={activeTab === TAB_ALL}
            >
              All changes
            </ButtonBase>
          </Box>
        )}
        {renderHeaderActorFilter()}
        <IconButton
          size="small"
          className={classes.closeButton}
          onClick={onClose}
          aria-label="Close change log panel"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box className={classes.tabContent}>
        {activeTab === TAB_SUMMARY || isSingleAdUnitSelected ? (
          actorFilteredChanges.length === 0 ? (
            <Typography variant="body2" color="textSecondary" className={listClasses.empty} style={{padding: 16}}>
              {uniqueChanges.length === 0
                ? 'No changes recorded for this period.'
                : 'No changes match the selected user.'}
            </Typography>
          ) : (
            <PanelChangeSummary
              changes={actorFilteredChanges}
              fill
              onSelectAdUnit={handleSelectAdUnit}
              hideScopeBadges={isSingleAdUnitSelected}
              attributeGroups={analyticsAttributeGroups}
              actorView={actorView}
            />
          )
        ) : (
          <>
            {showSearchBar && (
              <Box className={classes.searchBar}>
                <TextField
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder="Search changes…"
                  size="small"
                  fullWidth
                  variant="outlined"
                  autoComplete="off"
                  InputProps={{
                    className: classes.searchInput,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="disabled" />
                      </InputAdornment>
                    ),
                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          aria-label="Clear search"
                          onClick={() => setSearch('')}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Box>
            )}

            <Box className={`${listClasses.list} ${shouldVirtualize ? classes.listVirtual : ''}`}>
              {uniqueChanges.length === 0 ? (
                <Typography variant="body2" color="textSecondary" className={listClasses.empty}>
                  No changes recorded for this period.
                </Typography>
              ) : actorFilteredChanges.length === 0 ? (
                <Typography variant="body2" color="textSecondary" className={listClasses.empty}>
                  No changes match the selected user.
                </Typography>
              ) : filteredChanges.length === 0 ? (
                <Typography variant="body2" color="textSecondary" className={listClasses.empty}>
                  No changes match “{search.trim()}”.
                </Typography>
              ) : shouldVirtualize ? (
                <VariableSizeList
                  ref={listRef}
                  height={listVirtualHeight}
                  width="100%"
                  itemCount={items.length}
                  itemSize={getItemSize}
                  estimatedItemSize={ESTIMATED_ROW_HEIGHT}
                  itemKey={index => items[index].key}
                  itemData={virtualItemData}
                >
                  {VirtualRow}
                </VariableSizeList>
              ) : (
                items.map(item =>
                  item.type === 'header' ? (
                    <AdUnitHeader
                      key={item.key}
                      classes={listClasses}
                      group={item.group}
                      onOpen={() => handleSelectAdUnit(item.group)}
                    />
                  ) : (
                    <ChangeRow
                      key={item.key}
                      classes={listClasses}
                      change={item.change}
                      flush={item.flush}
                      attributeGroups={analyticsAttributeGroups}
                      actorView={actorView}
                    />
                  )
                )
              )}
            </Box>
          </>
        )}
      </Box>
      </Paper>
      {position?.placement === 'above' && (
        <span className={classes.arrowDownClip} style={{left: position.arrowLeft}} aria-hidden>
          <span className={classes.arrowDown} />
        </span>
      )}
      {position?.placement === 'below' && (
        <span className={classes.arrowUpClip} style={{left: position.arrowLeft}} aria-hidden>
          <span className={classes.arrowUp} />
        </span>
      )}
    </Box>
  );
};

export default React.memo(PanelChangeDetailPanel);
